import React from "react";
import { useSearchParams } from "react-router-dom";
import moment from "moment";
import Seo from "../../components/Seo";
import { EmptyState, LoadMore, PageHeader, SegmentTabs, Spinner, usePagedList } from "../../components/lawyer/LawyerUi";
import { lawyerService } from "../../services/lawyerApi";

const TABS = [
  { key: "services", label: "Services", stat: "service_earning" },
  { key: "invoices", label: "Invoice", stat: "invoice_earning" },
  { key: "calls", label: "Call", stat: "call_earning" },
];

const money = (v) => `$${Number((Number(v) || 0).toFixed(2)).toLocaleString()}`;
const net = (gross, commission) => (Number(gross) || 0) - (Number(commission) || 0);

const periodLabel = (period) => {
  if (period === "monthly") return "Monthly Subscription";
  if (period === "weekly") return "Weekly Subscription";
  return "One Time Service";
};

// Normalises the three different payload shapes into one row model.
const toRow = (tab, item) => {
  if (tab === "invoices") {
    const inv = item.invoice_data || {};
    return {
      id: item.id,
      title: `Invoice paid by ${item.chat?.user?.name || "client"}`,
      subtitle: inv.title ? `#${item.id} · ${inv.title}` : `Invoice #${item.id}`,
      date: item.created_at,
      amount: net(inv.total_amount, inv.commission_amount),
    };
  }
  if (tab === "calls") {
    const minutes = item.package?.duration_minutes;
    return {
      id: item.id,
      title: `Call with ${item.user?.name || "client"}`,
      subtitle: [item.category?.name, minutes ? `${minutes} Minutes` : null].filter(Boolean).join(" · ") || "Consultation call",
      date: item.started_at || item.created_at,
      amount: net(item.amount_paid, item.commission_amount),
    };
  }
  const meta = item.meta_data || {};
  return {
    id: item.id,
    title: `Payment received from ${item.user?.name || "client"}`,
    subtitle: periodLabel(meta.period),
    date: item.created_at,
    amount: meta.pay_amount !== undefined ? net(meta.pay_amount, meta.commission_amount) : Number(item.amount) || 0,
  };
};

const LawyerEarnings = () => {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === params.get("tab")) ? params.get("tab") : "services";
  const current = TABS.find((t) => t.key === tab);
  const list = usePagedList((p) => lawyerService.getEarnings(tab, p), (d) => d[tab], [tab]);
  const stats = list.meta.stats || {};

  return (
    <div className="lwp-page lwp-page-narrow">
      <Seo title="Total Earning | Legal Platform" noindex />
      <PageHeader title="Total Earning" />
      <SegmentTabs
        label="Earning source"
        value={tab}
        onChange={(key) => setParams(key === "services" ? {} : { tab: key }, { replace: true })}
        tabs={TABS}
      />

      <section className="lwp-earning-hero">
        <p>My Total Earning</p>
        <h2>{list.loading && !list.meta.stats ? <span className="lwp-skel lwp-skel-amount" /> : money(stats[current.stat])}</h2>
        {stats.total_earning !== undefined && (
          <span className="lwp-earning-hero-all">All sources: {money(stats.total_earning)}</span>
        )}
      </section>

      {list.loading ? (
        <Spinner />
      ) : list.meta.error ? (
        <EmptyState icon="bi-exclamation-circle" text={list.meta.error} />
      ) : list.items.length === 0 ? (
        <EmptyState icon="bi-wallet2" text={`No ${current.label.toLowerCase()} earnings yet.`} />
      ) : (
        <>
          <ul className="lwp-card-list">
            {list.items.map((item) => {
              const row = toRow(tab, item);
              const when = moment(row.date);
              return (
                <li key={row.id} className="lwp-earning-row">
                  <div className="lwp-earning-row-body">
                    <strong>{row.title}</strong>
                    <span>{row.subtitle}</span>
                    {when.isValid() && <time>{when.format("DD/MM/YYYY")}</time>}
                  </div>
                  <div className="lwp-earning-row-total">
                    <b>Total</b>
                    <span>+ {money(row.amount).replace("$", "$ ")}</span>
                  </div>
                </li>
              );
            })}
          </ul>
          <LoadMore list={list} />
        </>
      )}
    </div>
  );
};

export default LawyerEarnings;
